// kfe-music-track.js — music track player with synchronised bilingual lyrics.
//
// Brief 012. Ported from rhythmic mandarin web's `src/screens/rm-player.js`
// (AudioLyrics branch only), adapted to V1 conventions.
//
// Architectural choices (deviations from the rhythmic mandarin original):
//
//   • Owns its own HTMLAudioElement. V1's `core/audio-engine.js` is a
//     single-shot word player whose `play()` resolves on `ended`; it isn't
//     suited to long-form pause / resume / scrub. CLAUDE.md / Brief 012
//     authorise isolating the player here rather than refactoring the
//     shared engine.
//
//   • Lyric line resolution is done locally by `findActiveLineIndex` in
//     `engines/lyrics-sync.js` (pure function, no DOM). Rhythmic mandarin
//     wraps the same binary search in a stateful `LyricsEngine` class; the
//     stateless helper is enough here because the screen already tracks
//     `_activeLine` for diffing.
//
//   • Sync cadence is a requestAnimationFrame loop while playing. The
//     browser `timeupdate` event fires only ~4Hz, which can drift up to
//     ~250ms behind a fast-moving lyric — over the brief's <100ms tolerance.
//     The rAF loop reads `audio.currentTime` at display refresh rate.
//
// CMS contract (see `docs/CONTENT-CONTRACT-2026-05-04.md` §`music_track`):
//
//   {
//     id, type: "music_track",
//     audioUrl,            // e.g. "media/tracks/FF_EP003.m4a"
//     imageUrl,            // optional background image
//     lyricsRef,           // e.g. "lyrics/lyric_FF_EP003.json"
//     durationSec          // optional hint, real value comes from <audio>.duration
//   }
//
// Lyric JSON shape:
//   { id, playtimeUnit: "seconds", lines: [{playtime, lyricEn, lyricZh}, ...] }

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t, getLanguage } from "../core/i18n.js";
import { services } from "../platform-services.js";
import { findActiveLineIndex, normaliseLyricLines } from "../engines/lyrics-sync.js";

/** @typedef {import("../engines/lyrics-sync.js").LyricLine} LyricLine */

/**
 * Resolve a content path (either absolute http(s):// or relative like
 * "media/tracks/x.m4a") against the local `/content/` mount used in dev.
 * In production this will instead resolve against the signed-URL endpoint
 * once Brief 030 lands; for now we just prefix `/content/`.
 * @param {string} ref
 */
function resolveContentUrl(ref) {
  if (!ref) return ref;
  if (/^https?:\/\//i.test(ref)) return ref;
  if (ref.startsWith("/")) return ref;
  return `/content/${ref}`;
}

const SAVE_INTERVAL_MS = 5000;

export class KFEMusicTrack extends KFEComponent {
  // `lines`, `audioUrl`, `imageUrl` are settable directly for synthetic
  // testing. `itemId` is the production path (resolved against the items
  // store on connect).
  static props = ["itemId"];

  static styles = `
    :host {
      display: block;
      max-width: 56rem;
      margin: 0 auto;
      padding: 1rem;
      box-sizing: border-box;
    }

    .hero {
      position: relative;
      border-radius: 0.75rem;
      overflow: hidden;
      background: var(--kfe-surface);
      aspect-ratio: 16 / 9;
      margin-bottom: 1rem;
    }
    .hero img {
      width: 100%; height: 100%; object-fit: cover; display: block;
      opacity: 0.85;
    }
    .hero .overlay {
      position: absolute; inset: 0;
      background: linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.55) 100%);
      pointer-events: none;
    }
    .hero h2 {
      position: absolute; left: 1rem; right: 1rem; bottom: 0.75rem;
      margin: 0; font-size: 1.25rem; color: #fff;
      text-shadow: 0 1px 2px rgba(0,0,0,0.6);
    }

    .controls {
      display: flex; align-items: center; gap: 0.75rem;
      margin-bottom: 0.75rem;
    }
    button.play {
      flex: 0 0 auto;
      width: 3rem; height: 3rem;
      border-radius: 50%;
      background: var(--kfe-accent);
      color: var(--kfe-on-accent);
      border: 0; cursor: pointer;
      font-size: 1.15rem;
    }
    button.play:disabled { opacity: 0.5; cursor: default; }

    kfe-music-bar { display: block; flex: 1; }

    .lyrics-wrap {
      background: var(--kfe-surface);
      border-radius: 0.5rem;
      padding: 0.5rem;
      max-height: 50vh;
      overflow: auto;
    }

    .status {
      color: var(--kfe-muted);
      font-size: 0.8rem;
      margin-top: 0.5rem;
      min-height: 1.2em;
    }
    .error { color: #e57373; }
    .placeholder {
      padding: 2rem;
      text-align: center;
      color: var(--kfe-muted);
    }

    @media (max-width: 480px) {
      :host { padding: 0.75rem; }
      .hero { aspect-ratio: 1 / 1; }
    }
  `;

  constructor() {
    super();
    /** @type {HTMLAudioElement | null} */ this._audio = null;
    /** @type {LyricLine[]} */            this._lines = [];
    /** @type {number} */                  this._activeLine = -1;
    /** @type {number | null} */           this._rafId = null;
    /** @type {boolean} */                 this._playing = false;
    /** @type {string} */                  this._statusText = "";
    /** @type {string} */                  this._errorText = "";
    /** @type {number} */                  this._lastSavedMs = 0;

    // Resolved item data (CMS-shape, set in onConnected).
    /** @type {string} */ this._title = "";
    /** @type {string} */ this._audioUrl = "";
    /** @type {string} */ this._imageUrl = "";
    /** @type {string} */ this._lyricsRef = "";
    /** @type {number} */ this._durationHint = 0;
    /** @type {boolean} */ this._loaded = false;
    /** @type {boolean} */ this._noContent = false;
    /** @type {boolean} */ this._completionPersisted = false;
  }

  async onConnected() {
    // The legacy items JSON may be absent on dev installations that haven't
    // synced StreamingAssets. Swallow the load failure — music_track items
    // come from the CMS, not from `kfe_content_items.json`, so we can still
    // render anything passed in via direct props.
    let item;
    try {
      await services.items.loadContentItems();
      const id = /** @type {any} */ (this).itemId;
      item = id != null ? services.items.getItem(id) : undefined;
    } catch (e) {
      console.warn("[music-track] item lookup unavailable", e);
    }
    /** @type {any} */ const cms = item || {};

    // CMS-shaped fields. Legacy ContentItem doesn't carry these, so they're
    // undefined for now — until the CMS loader lands the screen renders a
    // placeholder.
    this._title = pickLocalised(cms.titleEn, cms.titleZh) || pickLocalised(cms.titleEn, cms.titleZh) || "Music track";
    this._audioUrl = String(cms.audioUrl || "");
    this._imageUrl = String(cms.imageUrl || "");
    this._lyricsRef = String(cms.lyricsRef || "");
    this._durationHint = Number(cms.durationSec) || 0;

    if (!this._audioUrl) {
      this._noContent = true;
      this._loaded = true;
      this._scheduleRender();
      return;
    }

    // Fetch lyrics (if any) before first paint so the strip renders fully.
    if (this._lyricsRef) {
      try {
        const r = await fetch(resolveContentUrl(this._lyricsRef), { cache: "force-cache" });
        if (r.ok) {
          const j = await r.json();
          const raw = Array.isArray(j?.lines) ? j.lines : [];
          this._lines = normaliseLyricLines(raw);
        } else {
          console.warn("[music-track] lyrics fetch failed", r.status, this._lyricsRef);
        }
      } catch (e) {
        console.warn("[music-track] lyrics fetch error", e);
      }
    }

    this._loaded = true;
    this._scheduleRender();
    // Wait for the next render pass to wire up <audio> + UI refs.
    queueMicrotask(() => this._wireAudio());
  }

  // ── Audio wiring ─────────────────────────────────────────────────────────

  _wireAudio() {
    const root = this.shadowRoot;
    if (!root) return;
    const audio = /** @type {HTMLAudioElement | null} */ (root.getElementById("track-audio"));
    if (!audio) return;
    this._audio = audio;

    audio.src = resolveContentUrl(this._audioUrl);
    audio.preload = "auto";

    /** @type {EventListener} */
    const onPlay = () => {
      this._playing = true;
      this._setStatus("Playing");
      this._startRafLoop();
      this._updatePlayButton();
    };
    /** @type {EventListener} */
    const onPause = () => {
      this._playing = false;
      this._setStatus("Paused");
      this._stopRafLoop();
      this._updatePlayButton();
      this._syncBarAndLyrics(audio.currentTime);
    };
    /** @type {EventListener} */
    const onEnded = () => {
      this._playing = false;
      this._setStatus("Ended");
      this._stopRafLoop();
      this._updatePlayButton();
      // Brief 073: a finished song is one "play". The item completes — and its
      // word-tagged media enters the SR Auto met-pool — only once
      // playCount >= requiredPlay, NOT on first song-end (per CMS §8.2).
      this._onSongEnded();
    };
    /** @type {EventListener} */
    const onError = () => {
      this._errorText = audio.error?.message || "Audio failed to load.";
      this._stopRafLoop();
      this._refreshStatusEl();
    };
    /** @type {EventListener} */
    const onLoaded = () => {
      this._refreshBar();
    };
    /** @type {EventListener} */
    const onTime = () => {
      // Backstop when the rAF loop is paused (audio not "playing" but the
      // user just scrubbed). Cheap; just nudges the UI.
      if (!this._playing) this._syncBarAndLyrics(audio.currentTime);
    };

    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);
    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("timeupdate", onTime);

    this.addCleanup(() => {
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("timeupdate", onTime);
      this._stopRafLoop();
      try { audio.pause(); } catch { /* swallow */ }
    });

    // Wire transport controls (created in render()).
    const playBtn = /** @type {HTMLButtonElement | null} */ (root.getElementById("play-btn"));
    if (playBtn) {
      playBtn.addEventListener("click", () => this._togglePlay());
    }

    const bar = root.getElementById("bar");
    if (bar) {
      const onSeek = /** @param {Event} e */ (e) => {
        const sec = Number(/** @type {CustomEvent<number>} */ (e).detail) || 0;
        this._seekTo(sec);
      };
      bar.addEventListener("seek", /** @type {EventListener} */ (onSeek));
      this.addCleanup(() => bar.removeEventListener("seek", /** @type {EventListener} */ (onSeek)));
    }

    const lyrics = root.getElementById("lyrics");
    if (lyrics) {
      /** @param {Event} e */
      const onLineSeek = (e) => {
        const d = /** @type {CustomEvent<{index:number, playtime:number}>} */ (e).detail;
        if (d && Number.isFinite(d.playtime)) this._seekTo(d.playtime);
      };
      lyrics.addEventListener("lineSeek", /** @type {EventListener} */ (onLineSeek));
      this.addCleanup(() => lyrics.removeEventListener("lineSeek", /** @type {EventListener} */ (onLineSeek)));
      /** @type {any} */ (lyrics).lines = this._lines;
      /** @type {any} */ (lyrics).activeIndex = -1;
    }

    this._refreshBar();
  }

  // ── Transport ────────────────────────────────────────────────────────────

  _togglePlay() {
    const a = this._audio;
    if (!a) return;
    if (a.paused) {
      // First call must be inside this user gesture (iOS Safari).
      const p = a.play();
      if (p && typeof p.catch === "function") {
        p.catch((err) => {
          this._errorText = String(err?.message || err || "play() failed");
          this._refreshStatusEl();
        });
      }
    } else {
      a.pause();
    }
  }

  /** @param {number} sec */
  _seekTo(sec) {
    const a = this._audio;
    if (!a) return;
    const dur = Number.isFinite(a.duration) ? a.duration : (this._durationHint || Infinity);
    const clamped = Math.max(0, Math.min(sec, Math.max(0, dur - 0.05)));
    a.currentTime = clamped;
    // Force an immediate UI refresh — `timeupdate` is browser-paced.
    this._syncBarAndLyrics(clamped);
  }

  // ── Sync loop ────────────────────────────────────────────────────────────

  _startRafLoop() {
    if (this._rafId != null) return;
    const tick = () => {
      this._rafId = null;
      const a = this._audio;
      if (!a) return;
      this._syncBarAndLyrics(a.currentTime);
      // Cheap periodic playhead save so a refresh / nav-away resumes
      // approximately where the learner was. Brief 030 / sync may upgrade
      // this to remote persistence; for now it's localStorage.
      const now = Date.now();
      if (now - this._lastSavedMs > SAVE_INTERVAL_MS) {
        this._lastSavedMs = now;
        this._savePlayhead(a.currentTime);
      }
      if (this._playing) this._rafId = requestAnimationFrame(tick);
    };
    this._rafId = requestAnimationFrame(tick);
  }

  _stopRafLoop() {
    if (this._rafId != null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  /** @param {number} t */
  _syncBarAndLyrics(t) {
    const root = this.shadowRoot;
    if (!root) return;
    const bar = /** @type {any} */ (root.getElementById("bar"));
    if (bar) bar.currentTimeSec = t;
    const idx = findActiveLineIndex(this._lines, t);
    if (idx !== this._activeLine) {
      this._activeLine = idx;
      const lyrics = /** @type {any} */ (root.getElementById("lyrics"));
      if (lyrics) lyrics.activeIndex = idx;
      this._scrollLineIntoView(idx);
    }
  }

  /** @param {number} idx */
  _scrollLineIntoView(idx) {
    const root = this.shadowRoot;
    if (!root || idx < 0) return;
    const lyrics = root.getElementById("lyrics");
    if (!lyrics) return;
    const target = lyrics.shadowRoot?.querySelector(`[data-i="${idx}"]`);
    if (target instanceof HTMLElement) {
      target.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }

  // ── Bar / status helpers ─────────────────────────────────────────────────

  _refreshBar() {
    const root = this.shadowRoot;
    if (!root) return;
    const bar = /** @type {any} */ (root.getElementById("bar"));
    if (!bar) return;
    const a = this._audio;
    bar.currentTimeSec = a?.currentTime || 0;
    const liveDur = a && Number.isFinite(a.duration) ? a.duration : 0;
    bar.durationSec = liveDur || this._durationHint || 0;
  }

  /** @param {string} text */
  _setStatus(text) {
    this._statusText = text;
    this._refreshStatusEl();
  }

  _refreshStatusEl() {
    const root = this.shadowRoot;
    if (!root) return;
    const s = root.getElementById("status");
    if (!s) return;
    s.classList.toggle("error", !!this._errorText);
    s.textContent = this._errorText || this._statusText || "";
  }

  _updatePlayButton() {
    const root = this.shadowRoot;
    if (!root) return;
    const btn = /** @type {HTMLButtonElement | null} */ (root.getElementById("play-btn"));
    if (!btn) return;
    btn.textContent = this._playing ? "❚❚" : "▶";
    btn.setAttribute("aria-label", this._playing ? "Pause" : "Play");
  }

  // ── Playhead persistence (lightweight, no learner-store coupling yet) ───

  /** @param {number} t */
  _savePlayhead(t) {
    const id = /** @type {any} */ (this).itemId;
    if (!id || !Number.isFinite(t)) return;
    try {
      localStorage.setItem(`kfe:music-track:${id}:playhead`, String(t));
    } catch { /* private mode or quota; ignore */ }
  }

  /**
   * Brief 073: handle one finished play of the track. Each `ended` bumps the
   * persisted `playCount`; the item only counts as completed (→ met-pool) once
   * `playCount >= requiredPlay`.
   *
   * `requiredPlay` is the item-level threshold (iOS `app_item_info.required_play`,
   * stored on the web `itemProgress` row). It defaults to 1, and the meter
   * `applyPlaycountAdjustment` path (Brief 055) can raise it — when it does, a
   * subsequent `ended` is compared against the NEW, higher threshold, so the
   * item correctly re-enters the "not yet complete" state until the learner
   * replays it enough times. That coordination is intentional.
   *
   * @returns {Promise<void>}
   */
  async _onSongEnded() {
    const id = Number(/** @type {any} */ (this).itemId);
    if (!Number.isFinite(id)) return;
    try {
      const playCount = await services.learner.incrementPlayCount(id);
      const progress = await services.learner.getItemProgress(id);
      const required = Math.max(1, progress?.requiredPlay ?? 1);
      const alreadyCompleted = (progress?.completedCount ?? 0) > 0;
      if (playCount >= required && !alreadyCompleted) this._markCompleted();
    } catch (err) {
      console.warn("[music-track] play-count update failed", err);
    }
  }

  /** Persist item completion once per view (one-shot guard, Brief 060b). */
  _markCompleted() {
    if (this._completionPersisted) return;
    const id = Number(/** @type {any} */ (this).itemId);
    if (!Number.isFinite(id)) return;
    this._completionPersisted = true;
    services.learner
      .markItemCompleted(id)
      .catch((err) => console.warn("[music-track] markItemCompleted failed", err));
  }

  // ── Render ───────────────────────────────────────────────────────────────

  render() {
    if (!this._loaded) {
      return html`<p class="placeholder">${t("loading")}</p>`;
    }
    if (this._noContent) {
      return html`
        <div class="placeholder">
          <p>Music track content is not yet available for this item.</p>
          <p style="font-size: 0.8rem; opacity: 0.7;">
            Waiting on CMS-published <code>audioUrl</code> / <code>lyricsRef</code>.
          </p>
        </div>
      `;
    }

    return html`
      <div class="hero">
        ${this._imageUrl
          ? html`<img src="${resolveContentUrl(this._imageUrl)}" alt="">`
          : html`<div class="overlay"></div>`}
        <div class="overlay"></div>
        <h2>${this._title}</h2>
      </div>

      <div class="controls">
        <button class="play" id="play-btn" type="button" aria-label="Play">▶</button>
        <kfe-music-bar id="bar"></kfe-music-bar>
      </div>

      <audio id="track-audio" preload="auto" crossorigin="anonymous"></audio>

      <div class="lyrics-wrap">
        <kfe-lyrics-display id="lyrics"></kfe-lyrics-display>
      </div>

      <div class="status" id="status"></div>
    `;
  }
}

/**
 * Pick the locale-appropriate string from a pair. Used for CMS-shaped items
 * that carry titleEn / titleZh side-by-side.
 * @param {string | undefined} en
 * @param {string | undefined} zh
 */
function pickLocalised(en, zh) {
  const lang = getLanguage();
  if (lang && lang.startsWith("zh")) return zh || en || "";
  return en || zh || "";
}
